<template>
  <div class="contact-page">
    <div class="contact-page__header">
      <h1 class="contact-page__title">Contact Us</h1>
      <p class="contact-page__subtitle">
        Have questions, feedback, or need support? Fill out the form below and we'll get back to you as soon as possible.
      </p>
    </div>

    <div class="contact-page__card">
      <form @submit.prevent="submitForm" class="contact-form">
        <div class="form-group">
          <label for="name">Name</label>
          <input
            type="text"
            id="name"
            v-model="name"
            placeholder="Your Name"
            required
          />
        </div>
        <div class="form-group">
          <label for="email">Email</label>
          <input
            type="email"
            id="email"
            v-model="email"
            placeholder="Your Email"
            required
          />
        </div>
        <div class="form-group">
          <label for="subject">Subject</label>
          <input
            type="text"
            id="subject"
            v-model="subject"
            placeholder="Subject"
            required
          />
        </div>
        <div class="form-group">
          <label for="message">Message</label>
          <textarea
            id="message"
            v-model="message"
            placeholder="Your Message"
            required
          ></textarea>
        </div>
        <button type="submit" class="submit-btn" :disabled="isSubmitting">
          <span v-if="isSubmitting">Sending...</span>
          <span v-else>Send Message</span>
        </button>
      </form>

      <transition name="fade">
        <p v-if="successMessage" class="status-message status-message--success">{{ successMessage }}</p>
      </transition>
      <transition name="fade">
        <p v-if="errorMessage" class="status-message status-message--error">{{ errorMessage }}</p>
      </transition>
    </div>
  </div>
</template>

<script>
import {sendContactEmail} from '@/api';

export default {
  name: 'ContactUs',
  data() {
    return {
      name: '',
      email: '',
      subject: '',
      message: '',
      isSubmitting: false,
      successMessage: '',
      errorMessage: '',
    };
  },
  methods: {
    async submitForm() {
      this.isSubmitting = true;
      this.successMessage = '';
      this.errorMessage = '';

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.email)) {
        this.errorMessage = 'Please enter a valid email address.';
        this.isSubmitting = false;
        return;
      }

      try {
        const {data} = await sendContactEmail({
          name: this.name,
          email: this.email,
          subject: this.subject,
          message: this.message,
        });

        if (data.success) {
          this.successMessage = 'Your message has been sent successfully. We will contact you soon.';
          this.name = '';
          this.email = '';
          this.subject = '';
          this.message = '';
        } else {
          this.errorMessage = 'Unexpected response from server.';
        }
      } catch (error) {
        console.error('Contact form error:', error);
        this.errorMessage = error.message || 'There was an error sending your message. Please try again later.';
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.contact-page {
  background: linear-gradient(180deg, var(--light-pink) 0%, #f5f0fa 100%);
  min-height: 100vh;
  padding: 0 1rem 3rem;
}

.contact-page__header {
  text-align: center;
  padding: 3rem 1rem 1.5rem;
  max-width: 550px;
  margin: 0 auto;
}

.contact-page__title {
  font-size: 2.2rem;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.75rem;
}

.contact-page__subtitle {
  font-size: 1.05rem;
  color: #555;
  line-height: 1.7;
}

.contact-page__card {
  max-width: 580px;
  margin: 0 auto;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
  animation: cardIn 0.5s ease both;
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

label {
  margin-bottom: 5px;
  font-weight: 600;
  font-size: 0.92rem;
  color: var(--violet-lush);
}

input,
textarea {
  padding: 10px 14px;
  font-size: 0.95rem;
  border-radius: 10px;
  border: 1.5px solid #ddd;
  background-color: white;
  color: var(--rich-black);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: var(--hollywood-cerise);
  box-shadow: 0 0 0 3px rgba(228, 19, 147, 0.1);
}

textarea {
  resize: vertical;
  min-height: 130px;
}

.submit-btn {
  padding: 14px 32px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--hollywood-cerise), var(--violet-lush));
  color: white;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 16px rgba(228, 19, 147, 0.3);
  align-self: center;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(228, 19, 147, 0.4);
}

.submit-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.status-message {
  margin-top: 1.25rem;
  text-align: center;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
}

.status-message--success {
  color: var(--violet-lush);
  background: rgba(89, 65, 127, 0.08);
}

.status-message--error {
  color: #e53e3e;
  background: rgba(229, 62, 62, 0.06);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .contact-page__title { font-size: 1.8rem; }
  .contact-page__card { padding: 1.5rem; border-radius: 16px; }
  input, textarea { font-size: 16px; padding: 12px 14px; }
  .submit-btn { width: 100%; }
}
</style>
